import {
  isLeafPlacement,
  isNavNode,
  LeafItem,
  LeafPlacement,
  NavNode,
  ResolvedChild,
  SelectableNode,
  StartStateResult,
  TreeCollection,
  TreeNode,
} from './types';

function parseDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function mergeValidity(
  base?: { validFrom?: string; validTo?: string },
  override?: { validFrom?: string; validTo?: string },
): { validFrom?: string; validTo?: string } {
  return {
    validFrom:
      override && override.validFrom !== undefined
        ? override.validFrom
        : base && base.validFrom,
    validTo:
      override && override.validTo !== undefined
        ? override.validTo
        : base && base.validTo,
  };
}

export function isVisibleByDate(
  validity: { validFrom?: string; validTo?: string },
  refDate: Date,
): boolean {
  if (validity.validFrom && refDate < parseDate(validity.validFrom)) {
    return false;
  }
  if (validity.validTo) {
    const end = parseDate(validity.validTo);
    end.setDate(end.getDate() + 1);
    if (refDate >= end) {
      return false;
    }
  }
  return true;
}

export function resolvePlacement(
  placement: LeafPlacement,
  items: Record<string, LeafItem>,
): ResolvedChild | null {
  const item = items[placement.itemRef];
  if (!item) {
    return null;
  }

  const validity = mergeValidity(item, placement);
  const name = placement.name !== undefined ? placement.name : item.name;

  return {
    id: placement.id,
    kind: 'leaf',
    name,
    title: item.title || name,
    image: placement.cardImage !== undefined ? placement.cardImage : item.image,
    leafImage: item.image,
    leafType: item.leafType,
    leafStyle: item.leafStyle || 'standard',
    content: item.content,
    componentId: item.componentId,
    validFrom: validity.validFrom,
    validTo: validity.validTo,
  };
}

export function resolveNavNode(
  node: NavNode,
  items: Record<string, LeafItem>,
  refDate: Date,
): ResolvedChild | null {
  const children = resolveChildren(node.children, items, refDate);
  if (children.length === 0) {
    return null;
  }

  if (!isVisibleByDate(node, refDate)) {
    return null;
  }

  return {
    id: node.id,
    kind: 'nav',
    name: node.name,
    title: node.name,
    image: node.image,
    validFrom: node.validFrom,
    validTo: node.validTo,
    children,
    navNode: node,
  };
}

export function resolveChildren(
  nodes: TreeNode[],
  items: Record<string, LeafItem>,
  refDate: Date,
): ResolvedChild[] {
  const resolved: ResolvedChild[] = [];

  for (const node of nodes) {
    let child: ResolvedChild | null = null;

    if (isNavNode(node)) {
      child = resolveNavNode(node, items, refDate);
    } else if (isLeafPlacement(node)) {
      child = resolvePlacement(node, items);
      if (child && !isVisibleByDate(child, refDate)) {
        child = null;
      }
    }

    if (child) {
      resolved.push(child);
    }
  }

  return resolved;
}

export function getResolvedRoots(
  collection: TreeCollection,
  refDate: Date = new Date(),
): ResolvedChild[] {
  return resolveChildren(collection.children, collection.items, refDate);
}

export function filterTreeByNodeIds(
  collection: TreeCollection,
  nodeIds: string[] | null | undefined,
  refDate: Date = new Date(),
): TreeCollection {
  if (nodeIds === null || nodeIds === undefined) {
    return collection;
  }

  const idSet = new Set(nodeIds);

  function filterNodes(nodes: TreeNode[]): TreeNode[] {
    const filtered: TreeNode[] = [];

    for (const node of nodes) {
      if (isNavNode(node)) {
        const childNodes = filterNodes(node.children);
        if (childNodes.length > 0 || idSet.has(node.id)) {
          filtered.push({ ...node, children: childNodes });
        }
      } else if (isLeafPlacement(node) && idSet.has(node.id)) {
        filtered.push(node);
      }
    }

    return filtered;
  }

  return {
    ...collection,
    children: filterNodes(collection.children),
  };
}

export function findNavNodeInPath(
  roots: ResolvedChild[],
  pathIds: string[],
): ResolvedChild[] {
  if (!pathIds.length) {
    return roots;
  }

  let current = roots;
  for (const id of pathIds) {
    const next = current.find((c) => c.id === id && c.kind === 'nav');
    if (!next || !next.children) {
      return [];
    }
    current = next.children;
  }
  return current;
}

function collectSubtreeIds(
  nodes: TreeNode[],
  items: Record<string, LeafItem>,
  refDate: Date,
): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (isNavNode(node)) {
      if (!isVisibleByDate(node, refDate)) {
        continue;
      }
      ids.push(node.id);
      ids.push.apply(ids, collectSubtreeIds(node.children, items, refDate));
    } else if (isLeafPlacement(node)) {
      const resolved = resolvePlacement(node, items);
      if (!resolved || !isVisibleByDate(resolved, refDate)) {
        continue;
      }
      ids.push(node.id);
    }
  }
  return ids;
}

function walkSelectableNodes(
  nodes: TreeNode[],
  items: Record<string, LeafItem>,
  depth: number,
  parentId: string | null,
  refDate: Date,
  result: SelectableNode[],
): void {
  for (const node of nodes) {
    if (isNavNode(node)) {
      if (!isVisibleByDate(node, refDate)) {
        continue;
      }
      const childIds = collectSubtreeIds(node.children, items, refDate);
      result.push({
        id: node.id,
        name: node.name,
        kind: 'nav',
        depth,
        parentId,
        childIds,
      });
      walkSelectableNodes(
        node.children,
        items,
        depth + 1,
        node.id,
        refDate,
        result,
      );
    } else if (isLeafPlacement(node)) {
      const resolved = resolvePlacement(node, items);
      if (!resolved || !isVisibleByDate(resolved, refDate)) {
        continue;
      }
      result.push({
        id: node.id,
        name: resolved.name,
        kind: 'leaf',
        depth,
        parentId,
        childIds: [],
      });
    }
  }
}

interface NodeLocation {
  pathNavIds: string[];
  leafPlacementId: string | null;
}

function findNodeLocation(
  nodes: TreeNode[],
  items: Record<string, LeafItem>,
  targetId: string,
  parentNavPath: string[],
  refDate: Date,
): NodeLocation | null {
  for (const node of nodes) {
    if (isNavNode(node)) {
      if (!isVisibleByDate(node, refDate)) {
        continue;
      }
      const navPath = parentNavPath.concat(node.id);
      if (node.id === targetId) {
        return {
          pathNavIds: navPath,
          leafPlacementId: null,
        };
      }
      const found = findNodeLocation(
        node.children,
        items,
        targetId,
        navPath,
        refDate,
      );
      if (found) {
        return found;
      }
    } else if (isLeafPlacement(node)) {
      const resolved = resolvePlacement(node, items);
      if (!resolved || !isVisibleByDate(resolved, refDate)) {
        continue;
      }
      if (node.id === targetId) {
        return {
          pathNavIds: parentNavPath,
          leafPlacementId: node.id,
        };
      }
    }
  }
  return null;
}

export function collectSelectableNodes(
  collection: TreeCollection,
  refDate: Date = new Date(),
): SelectableNode[] {
  const result: SelectableNode[] = [];
  walkSelectableNodes(
    collection.children,
    collection.items,
    0,
    null,
    refDate,
    result,
  );
  return result;
}

export function getAllSelectableNodeIds(
  collection: TreeCollection,
  refDate: Date = new Date(),
): string[] {
  return collectSelectableNodes(collection, refDate).map((n) => n.id);
}

export function getDescendantIds(
  collection: TreeCollection,
  nodeId: string,
  refDate: Date = new Date(),
): string[] {
  const nodes = collectSelectableNodes(collection, refDate);
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) {
    return [];
  }
  if (node.kind === 'leaf') {
    return [nodeId];
  }
  return [nodeId].concat(node.childIds);
}

export function resolveStartState(
  collection: TreeCollection,
  startNodeId: string,
  selectedNodeIds?: string[] | null,
  refDate: Date = new Date(),
): StartStateResult | null {
  if (startNodeId === collection.id) {
    return {
      pathNavIds: [],
      leafPlacementId: null,
    };
  }

  const filtered = filterTreeByNodeIds(collection, selectedNodeIds, refDate);
  const location = findNodeLocation(
    filtered.children,
    filtered.items,
    startNodeId,
    [],
    refDate,
  );
  if (!location) {
    return null;
  }
  return {
    pathNavIds: location.pathNavIds,
    leafPlacementId: location.leafPlacementId,
  };
}
